import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { money } from '../../lib/money';
import { Link } from 'react-router-dom';
import { Badge, Button, ConfirmDialog, DataTable, Drawer, EmptyState, Input, KpiTile, Select, Spinner, Textarea, useToast } from '../../ui/components';
import { IconButton } from '../../ui/components/Button';
import { Icon } from '../../ui/icons';
import { useCommercialSettings } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { catalogApi } from './catalogApi';

const SOURCE_TONE = { Manual: 'neutral', PurchaseOrder: 'brand', Receiving: 'success', Import: 'warning' };

// Display labels for the server's source enum (kept in English; translated at render).
const SOURCE_LABEL = { Manual: 'Manual', PurchaseOrder: 'Purchase order', Receiving: 'Receiving', Import: 'Import' };

// Where each source document lives, so a purchase line can deep-link to its origin.
// These modules don't exist yet; the link activates automatically once a record carries
// a sourceDocumentId (i.e. when Purchasing/Receiving generate the history).
const SOURCE_ROUTE = { PurchaseOrder: '/purchase-orders', Receiving: '/receiving', Invoices: '/invoices' };

function ReferenceCell({ record }) {
  const t = useT();
  const label = record.reference || (record.sourceDocumentId ? t('View') : null);
  if (!label) return <span className="pp-cell-muted">—</span>;
  const route = SOURCE_ROUTE[record.source];
  if (record.sourceDocumentId && route) {
    return <Link to={`${route}/${record.sourceDocumentId}`}>{label}</Link>;
  }
  return label;
}

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function PurchaseHistoryTab({ item, suppliers = [], locations = [], canManage, onChanged }) {
  const toast = useToast();
  const t = useT();
  const { currencies, defaultCurrency } = useCommercialSettings();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const EMPTY = useMemo(
    () => ({ supplierId: item.preferredSupplierId || '', purchaseDate: todayISO(), quantity: '', priceAmount: '', priceCurrency: item.standardCost?.currency || defaultCurrency, locationId: '', reference: '', requestedBy: '', notes: '' }),
    [item, defaultCurrency],
  );
  const [form, setForm] = useState(EMPTY);

  const currencyOptions = useMemo(() => {
    const codes = [...currencies];
    if (form.priceCurrency && !codes.includes(form.priceCurrency)) codes.push(form.priceCurrency);
    return codes.map((c) => ({ value: c, label: c }));
  }, [currencies, form.priceCurrency]);

  const supplierName = (id) => suppliers.find((s) => s.id === id)?.name;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRecords(await catalogApi.listPurchases(item.id));
    } catch (err) {
      toast.error(t('Could not load purchase history'), { description: err?.message });
    } finally {
      setLoading(false);
    }
  }, [item.id, toast, t]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY); setDrawerOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!(Number(form.quantity) > 0)) { toast.error(t('Enter a quantity greater than 0.')); return; }
    setSubmitting(true);
    try {
      await catalogApi.addPurchase(item.id, {
        supplierId: form.supplierId || null,
        purchaseDate: new Date(form.purchaseDate).toISOString(),
        quantity: Number(form.quantity),
        unitPrice: form.priceAmount === '' ? null : { amount: Number(form.priceAmount), currency: (form.priceCurrency || defaultCurrency).toUpperCase() },
        locationId: form.locationId || null,
        reference: form.reference.trim() || null,
        requestedByName: form.requestedBy.trim() || null,
        notes: form.notes.trim() || null,
      });
      toast.success(t('Purchase recorded'));
      setDrawerOpen(false);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(t('Could not record purchase'), { description: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await catalogApi.removePurchase(item.id, deleteTarget.id);
      toast.success(t('Record deleted'));
      setDeleteTarget(null);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(t('Could not delete record'), { description: err?.message });
    } finally {
      setDeleting(false);
    }
  };

  const summary = useMemo(() => {
    if (records.length === 0) return null;
    const totalQty = records.reduce((s, r) => s + (r.quantity || 0), 0);
    const currency = records[0].totalCost?.currency || records[0].unitPrice?.currency || 'USD';
    const totalSpend = records.reduce((s, r) => s + (r.totalCost?.amount || 0), 0);
    const avg = totalQty > 0 ? totalSpend / totalQty : 0;
    const last = records.reduce((a, b) => (new Date(a.purchaseDate) > new Date(b.purchaseDate) ? a : b));
    return { totalQty, currency, totalSpend, avg, lastDate: last.purchaseDate };
  }, [records]);

  const supplierOptions = [{ value: '', label: t('— No supplier —') }, ...suppliers.map((s) => ({ value: s.id, label: s.name }))];
  const locationOptions = [{ value: '', label: t('— No location —') }, ...locations.map((l) => ({ value: l.id, label: l.name }))];

  const columns = [
    { key: 'date', header: t('Date'), width: 120, render: (r) => new Date(r.purchaseDate).toLocaleDateString() },
    { key: 'supplier', header: t('Supplier'), width: 180, render: (r) => r.supplierName || supplierName(r.supplierId) || <span className="pp-cell-muted">—</span> },
    { key: 'requestedBy', header: t('Requested by'), width: 160, render: (r) => r.requestedByName || <span className="pp-cell-muted">—</span> },
    { key: 'qty', header: t('Qty'), width: 90, align: 'right', render: (r) => r.quantity },
    { key: 'unit', header: t('Unit price'), width: 130, align: 'right', render: (r) => money(r.unitPrice) },
    { key: 'total', header: t('Total'), width: 140, align: 'right', render: (r) => money(r.totalCost) },
    { key: 'ref', header: t('Reference'), width: 140, render: (r) => <ReferenceCell record={r} /> },
    { key: 'source', header: t('Source'), width: 120, render: (r) => <Badge tone={SOURCE_TONE[r.source] || 'neutral'}>{t(SOURCE_LABEL[r.source] || r.source)}</Badge> },
    {
      key: 'actions', header: '', width: 60, align: 'right', resizable: false,
      render: (r) => canManage ? (
        <div className="pp-row-actions" onClick={(e) => e.stopPropagation()}>
          <IconButton aria-label={t('Delete record')} variant="ghost" size="icon-sm" onClick={() => setDeleteTarget(r)}><Icon.Trash size={15} /></IconButton>
        </div>
      ) : null,
    },
  ];

  if (loading) {
    return <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-12)' }}><Spinner size={24} /></div>;
  }

  return (
    <>
      {summary && (
        <div className="item-kpis">
          <KpiTile label={t('Purchases')} value={records.length} />
          <KpiTile label={t('Total quantity')} value={summary.totalQty} />
          <KpiTile label={t('Total spend')} value={money({ amount: summary.totalSpend, currency: summary.currency })} />
          <KpiTile label={t('Avg unit price')} value={money({ amount: summary.avg, currency: summary.currency })} />
          <KpiTile label={t('Last purchase')} value={new Date(summary.lastDate).toLocaleDateString()} />
        </div>
      )}

      <div className="pp-toolbar">
        <div className="pp-toolbar__spacer" />
        {canManage && <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={openCreate}>{t('Record purchase')}</Button>}
      </div>

      {records.length === 0 ? (
        <EmptyState
          icon={<Icon.Receipt size={28} />}
          title={t('No purchase history')}
          description={t("Record past purchases manually, or they'll appear here automatically once purchase orders and receiving are live.")}
          action={canManage ? <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={openCreate}>{t('Record purchase')}</Button> : null}
        />
      ) : (
        <DataTable columns={columns} data={records} rowKey={(r) => r.id} density="comfortable" />
      )}

      <Drawer open={drawerOpen} onClose={() => (submitting ? null : setDrawerOpen(false))} title={t('Record purchase')} description={t('Log a historical purchase of this item.')} size="md">
        <form className="pp-form" onSubmit={save}>
          <div className="pp-form__row">
            <Input label={t('Date')} type="date" value={form.purchaseDate} onChange={(e) => setForm((f) => ({ ...f, purchaseDate: e.target.value }))} />
            <div className="ps-field">
              <span className="ps-field__label">{t('Supplier')}</span>
              <Select value={form.supplierId} onChange={(v) => setForm((f) => ({ ...f, supplierId: v }))} options={supplierOptions} ariaLabel={t('Supplier')} searchable />
            </div>
          </div>
          <div className="pp-form__row">
            <Input label={t('Quantity')} type="number" value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))} autoFocus />
            <div className="ps-field">
              <span className="ps-field__label">{t('Location')}</span>
              <Select value={form.locationId} onChange={(v) => setForm((f) => ({ ...f, locationId: v }))} options={locationOptions} ariaLabel={t('Location')} searchable />
            </div>
          </div>
          <div className="pp-form__row">
            <Input label={t('Unit price')} type="number" placeholder="0.00" value={form.priceAmount} onChange={(e) => setForm((f) => ({ ...f, priceAmount: e.target.value }))} />
            <div className="ps-field">
              <span className="ps-field__label">{t('Currency')}</span>
              <Select value={form.priceCurrency} onChange={(v) => setForm((f) => ({ ...f, priceCurrency: v }))} options={currencyOptions} ariaLabel={t('Currency')} />
            </div>
          </div>
          <div className="pp-form__row">
            <Input label={t('Reference')} placeholder={t('PO / invoice number')} value={form.reference} onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} />
            <Input label={t('Requested by')} placeholder={t('Who raised the request')} value={form.requestedBy} onChange={(e) => setForm((f) => ({ ...f, requestedBy: e.target.value }))} />
          </div>
          <Textarea label={t('Notes')} rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          <div className="pp-form__actions">
            <Button type="button" variant="ghost" onClick={() => setDrawerOpen(false)} disabled={submitting}>{t('Cancel')}</Button>
            <Button type="submit" variant="primary" loading={submitting}>{t('Record purchase')}</Button>
          </div>
        </form>
      </Drawer>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('Delete purchase record')}
        description={deleteTarget ? t('Delete the purchase from {date}?', { date: new Date(deleteTarget.purchaseDate).toLocaleDateString() }) : ''}
        confirmLabel={t('Delete')}
        tone="danger"
        loading={deleting}
        onConfirm={doDelete}
        onCancel={() => (deleting ? null : setDeleteTarget(null))}
      />
    </>
  );
}
